import React from 'react';
import { motion } from 'framer-motion';
import { 
  Cpu,
  Shield, 
  Lock, 
  Eye, 
  Bell, 
  Server, 
  Cloud,
  Database,
  CheckCircle,
  ArrowRight,
  Users,
  Clock,
  AlertTriangle,
  FileCheck,
  Network,
  Brain,
  Zap,
  Target
} from 'lucide-react';

interface QuantumSecurityProps {
  setCurrentPage: (page: string) => void;
}

const QuantumSecurity: React.FC<QuantumSecurityProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Enterprise-Grade Protection",
      description: "Military-grade security features for large-scale enterprise protection.",
      items: [
        "Zero-trust architecture",
        "Advanced threat intelligence",
        "Quantum-ready encryption",
        "Custom security controls"
      ]
    },
    {
      icon: Brain,
      title: "AI-Powered Security",
      description: "Advanced artificial intelligence and machine learning security.",
      items: [
        "Predictive threat detection",
        "Automated response",
        "Pattern recognition",
        "Anomaly detection"
      ]
    },
    {
      icon: Lock,
      title: "Advanced Access Control",
      description: "Sophisticated identity and access management system.",
      items: [
        "Biometric authentication",
        "Context-aware security",
        "Privileged access management",
        "Zero-trust implementation"
      ]
    },
    {
      icon: Eye,
      title: "Intelligent Monitoring",
      description: "Comprehensive security monitoring and analytics platform.",
      items: [
        "Real-time threat analysis",
        "Security orchestration",
        "Advanced correlation",
        "Predictive analytics"
      ]
    }
  ];

  const services = [
    {
      icon: Server,
      title: "Enterprise Infrastructure",
      description: "Comprehensive protection for complex enterprise environments.",
      items: [
        "Custom security architecture",
        "Advanced infrastructure security",
        "Enterprise-wide monitoring",
        "Automated response systems"
      ]
    },
    {
      icon: Cloud,
      title: "Multi-Cloud Security",
      description: "Advanced security for complex cloud environments.",
      items: [
        "Hybrid cloud protection",
        "Cloud security posture",
        "Workload protection",
        "Cloud access security"
      ]
    },
    {
      icon: Database,
      title: "Enterprise Data Security",
      description: "Advanced data protection for enterprise operations.",
      items: [
        "Data sovereignty",
        "Advanced encryption",
        "Data lifecycle security",
        "Privacy compliance"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Target,
      title: "Strategic Security",
      items: [
        "Security roadmap",
        "Risk management",
        "Compliance strategy",
        "Security architecture"
      ]
    },
    {
      icon: Zap,
      title: "Rapid Response",
      items: [
        "Incident response",
        "Threat hunting",
        "Breach containment",
        "Recovery operations"
      ]
    },
    {
      icon: Network,
      title: "Global Operations",
      items: [
        "24/7 SOC operations",
        "Global threat monitoring",
        "Security automation",
        "Continuous assessment"
      ]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Maximum Protection",
      description: "Enterprise-grade security for mission-critical operations"
    },
    {
      icon: Users,
      title: "Dedicated Team",
      description: "Dedicated security team and technical account manager"
    },
    {
      icon: Brain,
      title: "AI-Driven Security",
      description: "Advanced AI and ML-powered security operations"
    },
    {
      icon: Target,
      title: "Custom Solutions",
      description: "Tailored security solutions for your enterprise"
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Cpu className="h-16 w-16 text-green-400 animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Quantum Cybersecurity Package</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Enterprise-grade cybersecurity solution with advanced AI capabilities and custom security features for large organizations
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Enterprise Services</h2>
            <p className="text-lg text-muted">
              Advanced security services for enterprise organizations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{service.description}</p>

                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Key Benefits</h2>
            <p className="text-lg text-muted">
              Why choose Quantum Cybersecurity Package
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-lg mb-6">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advanced Capabilities</h2>
            <p className="text-lg text-muted">
              Enterprise-grade security capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>

                <ul className="space-y-3">
                  {capability.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready for Enterprise-Grade Security?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our security experts to learn more about our Quantum Cybersecurity Package and how it can protect your enterprise
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default QuantumSecurity;